class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    // Header search bars only: "before typing" panel, results target and close button.
    this.defaultPanel = this.querySelector('[data-predictive-search-default]');
    this.resultsTarget = this.querySelector('[data-predictive-search-results]') || this.predictiveSearchResults;
    this.closeButton = this.querySelector('[data-predictive-search-close]');
    // Header bars use predictive-search-panel; the search page keeps the default section.
    this.sectionId = this.dataset.sectionId || 'predictive-search';
    this.isPanel = Boolean(this.defaultPanel);
    // Mobile/tablet header bar (desktop uses details-modal)
    this.isMobileBar = this.isPanel && !this.closest('details-modal');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.isPanel) {
      this.input.addEventListener('focus', this.setPanelPlaceholder.bind(this));
      window.addEventListener('resize', () => {
        if (this.isOpen) this.open();
        // Rotating a tablet crosses the mobile breakpoint, so the short/long placeholder can change
        if (document.activeElement === this.input) this.setPanelPlaceholder();
      });
      // iOS keeps innerHeight when the keyboard opens, so refit to the visible area
      window.visualViewport?.addEventListener('resize', () => this.isOpen && this.fitPanelHeight());
      window.visualViewport?.addEventListener('scroll', () => this.isOpen && this.fitPanelHeight());
    }

    if (this.isMobileBar) {
      // First tap opens the panel without the keyboard; tapping the field again brings it up
      let touchStart = null;
      this.input.addEventListener(
        'touchstart',
        (event) => {
          touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        },
        { passive: true }
      );
      this.input.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        const moved = touchStart && Math.hypot(touch.clientX - touchStart.x, touch.clientY - touchStart.y) > 10;
        touchStart = null;
        if (this.isOpen || moved) return;
        event.preventDefault();
        this.setPanelPlaceholder();
        this.onFocus();
      });
      // Keyboard "Done" blurs the field, so close on a tap outside instead of on focusout
      document.addEventListener('click', (event) => {
        if (this.isOpen && !this.contains(event.target)) this.close();
      });
    }

    if (this.closeButton) {
      // Keeps focus in the input so focusout doesn't close the panel before the click lands
      this.closeButton.addEventListener('mousedown', (event) => event.preventDefault());
      this.closeButton.addEventListener('click', () => {
        this.close();
        this.input.blur();
      });
    }
  }

  // Fixed placeholder from the wireframe while the panel is open.
  setPanelPlaceholder() {
    this.alignToHeader();
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const placeholder = isMobile ? this.input.dataset.panelPlaceholderMobile : this.input.dataset.panelPlaceholder;
    if (!placeholder) return;
    if (this.originalPlaceholder === undefined) this.originalPlaceholder = this.input.getAttribute('placeholder');
    this.input.setAttribute('placeholder', placeholder);
  }

  restorePlaceholder() {
    if (this.originalPlaceholder === undefined) return;
    this.input.setAttribute('placeholder', this.originalPlaceholder);
    this.originalPlaceholder = undefined;
  }

  // Shows the "before typing" panel; returns false when it has nothing to show.
  showDefault() {
    if (!this.defaultPanel) return false;

    window.SearchRecentlyViewed?.render(this.defaultPanel);

    const panel = this.defaultPanel.querySelector('.ps-panel');
    const hasContent =
      panel &&
      (panel.dataset.hasMain === 'true' || panel.dataset.hasTop === 'true' || panel.dataset.hasRecent === 'true');
    if (!hasContent) {
      this.close();
      return false;
    }

    this.setAttribute('mode', 'default');
    this.open();
    window.SearchPanelCarousel?.refresh(this.defaultPanel);
    return true;
  }

  closeOrShowDefault(clearSearchTerm = false) {
    if (this.defaultPanel && document.activeElement === this.input) {
      this.closeResults(clearSearchTerm);
      this.showDefault();
      return;
    }
    this.close(clearSearchTerm);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.closeOrShowDefault(true);
      return;
    }

    this.setAttribute('mode', 'results');
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      if (this.defaultPanel) this.showDefault();
    }
  }

  onFocus() {
    // Panel already opened by the first tap; just let the keyboard come up
    if (this.isMobileBar && this.isOpen) return;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.isPanel) this.showDefault();
      return;
    }

    this.setAttribute('mode', 'results');

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (this.isMobileBar) return;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length && this.getAttribute('mode') !== 'default') this.closeOrShowDefault(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        if (this.closeButton && this.isOpen) {
          this.close();
          this.input.blur();
        }
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = `${this.sectionId}:${searchTerm.replace(' ', '-').toLowerCase()}`;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Up to 10 of each type, so other types don't eat into the product limit setting.
    // Header panel also lists the types (pages are left out otherwise).
    const typeParam = this.isPanel
      ? `&resources[type]=${this.dataset.resourceTypes || 'query,product,collection,page'}`
      : '';
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}${typeParam}&resources[limit]=10&resources[limit_scope]=each&section_id=${this.sectionId}`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(`#shopify-section-${this.sectionId}`).innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          if (predictiveSearchInstance.sectionId === this.sectionId) {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // Ignore a late response once the term is cleared
    if (this.isPanel && !this.getQuery().length) return;

    this.resultsTarget.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setAttribute('mode', 'results');
    window.SearchPanelCarousel?.refresh(this.resultsTarget);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  // Lines the search row and panel up with the header (logo/menu icon to cart icon; desktop row sits over the menu).
  alignToHeader() {
    const modal = this.closest('.search-modal');
    const header = this.closest('.header');
    if (!modal || !header || !this.offsetParent) return;

    const headerBox = header.getBoundingClientRect();
    const cart = header.querySelector('#cart-icon-bubble .svg-wrapper') || header.querySelector('#cart-icon-bubble');
    let start = this.input.getBoundingClientRect().left;

    if (this.closest('details-modal')) {
      const nav = header.querySelector('.header__inline-menu');
      const logo =
        header.querySelector(':scope > .header__heading .header__heading-logo, :scope > .header__heading-link .header__heading-logo') ||
        header.querySelector(':scope > .header__heading .header__heading-link, :scope > .header__heading-link');
      const top = nav && nav.offsetHeight ? nav.getBoundingClientRect().top - headerBox.top : headerBox.height;
      modal.style.setProperty('--ps-modal-top', `${Math.round(top)}px`);
      if (logo) start = logo.getBoundingClientRect().left;

      // Desktop: the open field sits exactly over the closed header pill (component-predictive-search-panel.css)
      const pill = this.closest('details-modal').querySelector('summary .search__input--wrapper');
      const row = this.closest('.search-modal__content');
      if (pill && row && pill.offsetWidth) {
        const pillBox = pill.getBoundingClientRect();
        const rowBox = row.getBoundingClientRect();
        // Row's final top is the modal top set above (it may not have moved there yet when this runs)
        modal.style.setProperty('--ps-field-top', `${Math.round(pillBox.top - headerBox.top - top)}px`);
        modal.style.setProperty('--ps-field-left', `${Math.round(pillBox.left - rowBox.left)}px`);
        modal.style.setProperty('--ps-field-width', `${Math.round(pillBox.width)}px`);
        modal.style.setProperty('--ps-field-height', `${Math.round(pillBox.height)}px`);
      }
    }

    modal.style.setProperty('--ps-gutter-start', `${Math.round(start - headerBox.left)}px`);
    if (cart) modal.style.setProperty('--ps-gutter-end', `${Math.round(headerBox.right - cart.getBoundingClientRect().right)}px`);
  }

  open() {
    this.setAttribute('open', true);
    // Before measuring: the lock hides the bars above the header on mobile
    this.togglePageScroll(true);

    if (!this.isPanel) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    } else {
      this.alignToHeader();
      this.fitPanelHeight();
    }
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  // Measured from the panel itself: .section-header has no height, so the theme's value overshoots.
  // Ends at the bottom of the visual viewport, i.e. above the iOS keyboard.
  fitPanelHeight() {
    const panelTop = this.predictiveSearchResults.getBoundingClientRect().top;
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    this.predictiveSearchResults.style.maxHeight =
      panelTop > 0 ? `${bottom - panelTop}px` : `${this.getResultsMaxHeight()}px`;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    if (this.isPanel) {
      this.restorePlaceholder();
      // A response still in flight would reopen the panel
      this.abortController.abort();
      this.abortController = new AbortController();
    }
    this.togglePageScroll(false);
  }

  // Mobile/tablet bar: only the panel scrolls while it's open (desktop's details-modal already locks the page)
  togglePageScroll(lock) {
    if (!this.isPanel || this.closest('details-modal')) return;
    document.documentElement.classList.toggle('ps-scroll-lock', lock);
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.removeAttribute('mode');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
